import SEOHead from "@/components/SEOHead";
import { ArrowRight, Users, RefreshCw, HeadphonesIcon } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import CTASection from "@/components/landing/CTASection";
import { useLanguage } from "@/contexts/LanguageContext";
import indoPacificClimateTech from "@/assets/indo-pacific-climate-tech.png";
import scgSasinBbc2023 from "@/assets/scg-sasin-bbc-2023.jpg";
import digitalDocuments from "@/assets/digital-documents.png";
import teamMeeting from "@/assets/team-meeting.png";
import tatAward from "@/assets/tat-award.jpg";
import cimbLogo from "@/assets/logos/cimb-bank.png";
import lhBankLogo from "@/assets/logos/lh-bank.png";
import pwcLogo from "@/assets/logos/pwc.png";
import setLogo from "@/assets/logos/set.png";
import bridgestoneLogo from "@/assets/logos/bridgestone.png";
const AboutUs = () => {
  const { t, language } = useLanguage();

  const metadata = {
    en: {
      title: "About Us | Carbonwize",
      description: "Learn about Carbonwize, Thailand's leading carbon management platform trusted by 500+ enterprises for compliance-ready emissions reporting.",
      locale: "en_US"
    },
    th: {
      title: "เกี่ยวกับเรา | Carbonwize",
      description: "เรียนรู้เกี่ยวกับ Carbonwize แพลตฟอร์มจัดการคาร์บอนชั้นนำของไทย ที่ได้รับความไว้วางใจจากองค์กรกว่า 500 แห่ง",
      locale: "th_TH"
    }
  };

  const ogTags = {
    title: "Carbonwize | Audit-Grade Carbon Management for Enterprises",
    description: "Automate carbon accounting with enterprise-grade compliance, precision, and automation. Trusted by 500+ enterprises."
  };

  const logos = [
    { name: "Stock Exchange of Thailand - Official Carbonwize partner", src: setLogo },
    { name: "CIMB Bank - Enterprise carbon accounting client", src: cimbLogo },
    { name: "LH Bank - Financial carbon management client", src: lhBankLogo },
    { name: "Bridgestone - Manufacturing sustainability client", src: bridgestoneLogo },
  ];

  const achievements = [
    {
      title: t("aboutUs.achievement1Title"),
      description: t("aboutUs.achievement1Desc"),
      url: "/blog/carbonwize-is-listed-as-top-100-indo-pacific-climate-tech",
      image: indoPacificClimateTech,
      category: "Award",
      isInternal: true,
    },
    {
      title: t("aboutUs.achievement2Title"),
      description: t("aboutUs.achievement2Desc"),
      url: "https://www.sasin.edu/content/news/team-cwallet-from-chiang-mai-university-wins-scg-bangkok-business-challenge-sasin-2023-thailand-competition-sasin-x-scgc-pledge-to-continue-advancing-global-growth-of-thai-startups",
      image: scgSasinBbc2023,
      category: "Award",
      isInternal: false,
    },
    {
      title: t("aboutUs.achievement3Title"),
      description: t("aboutUs.achievement3Desc"),
      url: "/blog/carbonwize-tat-travel-tech-startup-2024",
      image: tatAward,
      category: "Award",
      isInternal: true,
    },
  ];

  return (
    <>
      <SEOHead
        title={metadata[language].title}
        description={metadata[language].description}
        path="/about-us"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://carbonwize.io"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "About Us",
              "item": "https://carbonwize.io/about-us"
            }
          ]
        }}
      />

      <div className="min-h-screen bg-[#f9fafb]">
        <Navbar />

        {/* Hero Section - Centered */}
        <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-[#ebf8f0]">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-8">
              {t("aboutUs.title")}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              {t("aboutUs.intro")}
            </p>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mt-6">
              {t("aboutUs.mission")}
            </p>
          </div>

        </section>

        {/* Our Uniqueness Section - Image Left, Content Right */}
        <section className="pt-24 lg:pt-32 pb-16 lg:pb-24 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              {/* Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-lg h-64 sm:h-80 lg:h-96">
                <img 
                  src={teamMeeting} 
                  alt="Carbonwize team meeting - Collaborative carbon management strategy session" 
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              {/* Content */}
              <div className="flex flex-col justify-center">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-6">
                  {t("aboutUs.strengthsTitle")}
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                  {t("aboutUs.strengthsDesc")}
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {t("aboutUs.strengthsDetail")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Core Values Section - Content Left, Image Right */}
        <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              {/* Content */}
              <div className="flex flex-col justify-center order-2 lg:order-1">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-6">
                  {t("aboutUs.valuesTitle")}
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                  {t("aboutUs.valuesDesc1")}
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  <Link to="/#vvb-auditors" className="text-primary hover:underline font-medium">
                    {t("aboutUs.valuesVvbLink")}
                  </Link>{" "}
                  {t("aboutUs.valuesDesc2")}
                </p>
              </div>
              {/* Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-lg h-64 sm:h-80 lg:h-96 order-1 lg:order-2">
                <img 
                  src={digitalDocuments} 
                  alt="Carbonwize digital document management - Carbon audit verification and compliance documentation" 
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* What Sets Us Apart Section */}
        <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#ebf8f0]">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-cta mb-4">
                {t("aboutUs.differenceTitle")}
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {t("aboutUs.differenceSubtitle")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Difference 1 */}
              <div className="bg-card border border-border/50 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">
                  {t("aboutUs.difference1Title")}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t("aboutUs.difference1Desc")}
                </p>
              </div>

              {/* Difference 2 */}
              <div className="bg-card border border-border/50 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <RefreshCw className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">
                  {t("aboutUs.difference2Title")}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t("aboutUs.difference2Desc")}
                </p>
              </div>

              {/* Difference 3 */}
              <div className="bg-card border border-border/50 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <HeadphonesIcon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">
                  {t("aboutUs.difference3Title")}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t("aboutUs.difference3Desc")}
                </p>
              </div>
            </div>

            {/* Trusted By Logo Carousel */}
            <div className="mt-16 text-center">
              <h3 className="text-xl md:text-2xl font-bold text-foreground mb-8">
                {t("aboutUs.trustedBy")}
              </h3>
              <div className="relative overflow-hidden">
                <div className="flex animate-scroll-left items-center">
                  {logos.map((logo, index) => (
                    <div key={`first-${index}`} className="flex-shrink-0 mx-6 sm:mx-12 flex items-center justify-center px-3 sm:px-6">
                      <img src={logo.src} alt={logo.name} className="h-6 sm:h-8 w-auto object-contain" />
                    </div>
                  ))}
                  {logos.map((logo, index) => (
                    <div key={`second-${index}`} className="flex-shrink-0 mx-6 sm:mx-12 flex items-center justify-center px-3 sm:px-6">
                      <img src={logo.src} alt={logo.name} className="h-6 sm:h-8 w-auto object-contain" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Achievements Section - Colossal Preview Cards */}
        <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-secondary/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-cta">
                {t("aboutUs.achievementsTitle")}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mt-4">
                {t("aboutUs.achievementsDesc")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {achievements.map((achievement, index) => (
                <article
                  key={index}
                  className="group bg-card border border-border/50 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
                >
                  {achievement.isInternal ? (
                    <Link to={achievement.url} className="block">
                      <div className="relative h-48 overflow-hidden bg-muted">
                        <img
                          src={achievement.image}
                          alt={`${achievement.title} - Carbonwize sustainability award and recognition`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                            {achievement.category}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {achievement.title}
                        </h3>

                        <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                          {achievement.description}
                        </p>

                        <span className="text-primary font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                          {t("articles.readMore")}
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </Link>
                  ) : (
                    <a
                      href={achievement.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <div className="relative h-48 overflow-hidden bg-muted">
                        <img
                          src={achievement.image}
                          alt={`${achievement.title} - Carbonwize sustainability award and recognition`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                            {achievement.category}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {achievement.title}
                        </h3>

                        <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                          {achievement.description}
                        </p>

                        <span className="text-primary font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                          {t("articles.readMore")}
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </a>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>

        <CTASection />

        <Footer />
      </div>
    </>
  );
};

export default AboutUs;
